/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Dialog, Grid, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachButton } from 'bundles/ai-coach-platform/components/building-blocks';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import _t from 'i18n!nls/ai-coach-platform';

type Props = {
  onClose: () => void;
  onSubmit: () => void;
};

export const NewConversationModal = ({ onClose, onSubmit }: Props): React.ReactElement => {
  const { Content, Actions, HeadingGroup } = Dialog;
  const track = useTracker();
  const trackingData = useCoachTrackingContext()?.data;

  const handleOnSubmit = () => {
    track('interact_coach', {
      coachInteractionType: 'start_new_continuous_chat_session',
      coach: {
        mode: trackingData?.mode,
      },
      ...(trackingData?.courseId && {
        product: {
          type: 'course',
          id: trackingData.courseId as string,
        },
      }),
      ...(trackingData?.itemId && {
        courseItem: {
          id: trackingData.itemId as string,
        },
      }),
    });

    // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
    track('click_coach_element', {
      clickElementType: 'start_new_continuous_chat',
      coach: {
        mode: trackingData?.mode,
      },
      ...(trackingData?.courseId && {
        product: {
          type: 'course',
          id: trackingData.courseId as string,
        },
      }),
      ...(trackingData?.itemId && {
        courseItem: {
          id: trackingData.itemId as string,
        },
      }),
    });

    onSubmit();
    onClose();
  };

  const handleOnClose = () => {
    onClose();
  };

  return (
    <Dialog variant="transactional" aria-describedby="dialog-content" open={true} onClose={handleOnClose}>
      <HeadingGroup>{_t('Start a new conversation')}</HeadingGroup>
      <Content id="dialog-content">
        <Typography2
          variant="bodyPrimary"
          component="p"
          css={css`
            color: var(--cds-color-grey-800);
          `}
        >
          {_t("Starting a new chat will close your current discussion. You won't be able to return to it later.")}
        </Typography2>
      </Content>
      <Actions>
        <Grid container spacing={16}>
          <Grid item>
            <CoachButton variant="primary" onClick={handleOnSubmit}>
              {_t('Start fresh')}
            </CoachButton>
          </Grid>
          <Grid item>
            <CoachButton variant="secondary" onClick={handleOnClose}>
              {_t('Go back')}
            </CoachButton>
          </Grid>
        </Grid>
      </Actions>
    </Dialog>
  );
};

export default NewConversationModal;
